import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  flattenFormikErrorEntries,
  focusFirstFormikError,
} from "./agreementFormErrors";

/**
 * Visible validation summary + focus first field after a failed submit.
 */
export function AgreementFormErrorSummary({ formik }) {
  const { t } = useTranslation();
  const bannerRef = useRef(null);
  const lastFocusedSubmit = useRef(0);
  const entries = flattenFormikErrorEntries(formik?.errors);
  const show = Boolean(formik?.submitCount > 0 && entries.length);

  useEffect(() => {
    if (!show) return;
    if (formik.submitCount === lastFocusedSubmit.current) return;
    lastFocusedSubmit.current = formik.submitCount;
    const formEl = bannerRef.current?.closest("form");
    focusFirstFormikError(formik.errors, formEl || document);
  }, [show, formik?.submitCount, formik?.errors]);

  if (!show) return null;

  return (
    <div
      ref={bannerRef}
      role="alert"
      aria-live="polite"
      className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
    >
      <p className="font-semibold">
        {t("service.agreements.form.errors_summary")}
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        {entries.map((entry) => (
          <li key={`${entry.name}:${entry.message}`}>
            <button
              type="button"
              className="text-left underline decoration-rose-300 underline-offset-2 hover:decoration-rose-600"
              onClick={() => {
                const formEl = bannerRef.current?.closest("form");
                const scope = formEl || document;
                const el = scope.querySelector?.(
                  `[name="${entry.name}"]`
                );
                if (el?.focus) {
                  el.focus();
                  el.scrollIntoView?.({ behavior: "smooth", block: "center" });
                }
              }}
            >
              {entry.message}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
